import React from "react"
import {
  css,
  StyleSheet,
  styleResolver,
  STYLE_GROUPS,
} from "@design-system/style-sheet"
import { useStyleProps } from "../style-props"
import { As, DomProps } from "../types"

// Reset styles for heading, link, and list DOM elements
const classes = css.create(
  {
    reset: {
      backgroundColor: "transparent",
      color: "inherit",
      font: "inherit",
      listStyle: "none",
      margin: 0,
      textAlign: "inherit",
      textDecoration: "none",
    },
    cursor: {
      cursor: "pointer",
    },
  },
  STYLE_GROUPS.classicReset,
)

const pointerEventsStyles = StyleSheet.create({
  auto: {
    pointerEvents: "auto",
  },
  "box-none": {
    pointerEvents: "box-none",
  },
  "box-only": {
    pointerEvents: "box-only",
  },
  none: {
    pointerEvents: "none",
  },
})

export function createElement<T extends React.ElementType, P, TT extends As>(
  type: T,
  props = {} as P extends { as?: TT } ? Omit<P, "as"> & { as?: TT } : P,
  ...children: React.ReactNode[]
) {
  const {
    id,
    nativeID,
    testID,
    needsReset,
    needsCursor,
    pointerEvents,
    classList,
    as,
    accessibilityRole,
    accessibilityLabel,
    accessibilityLiveRegion,
    ...others
  } = props as DomProps & { as?: TT }
  const Component = as || type
  const { ...styledProps } = useStyleProps(others, Component)
  const domProps = { ...styledProps } as React.ComponentProps<any>
  const providedStyle = domProps.style

  const resetCss =
    needsReset ||
    (typeof Component === "string" &&
      ["a", "button", "li", "ul", "h1", "h2", "h3", "h4", "h5", "h6"].includes(
        Component as string,
      ))

  // STYLE
  const reactNativeStyle = StyleSheet.compose(
    pointerEvents && pointerEventsStyles[pointerEvents],
    providedStyle,
  )

  const finalClassList = [
    ...(classList ?? []),
    resetCss && classes.reset,
    needsCursor && classes.cursor,
  ].filter(Boolean) as string[]

  // Resolve styles
  const { className, style, classList: newClassList } = styleResolver.resolve(
    reactNativeStyle,
    finalClassList,
  )

  if (className != null && className !== "") {
    domProps.className = className
  }

  // pass classList to Customized Components
  // e.g. <Icon as={PhoneIcon} />
  if (
    as &&
    typeof as !== "string" &&
    Array.isArray(newClassList) &&
    newClassList.length
  ) {
    domProps.classList = newClassList
  }

  domProps.style = style

  if (nativeID || id) {
    domProps.id = nativeID || id
  }

  if (accessibilityRole != null) {
    domProps.role = accessibilityRole
  }

  // accessibilityLiveRegion
  if (accessibilityLiveRegion != null) {
    domProps["aria-live"] =
      accessibilityLiveRegion === "none" ? "off" : accessibilityLiveRegion
  }

  if (accessibilityLabel != null) {
    domProps["aria-label"] = accessibilityLabel
  }

  if (testID) {
    domProps["data-testid"] = testID
  }

  return React.createElement(Component, domProps, ...children)
}
